import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Car, 
  Users, 
  DollarSign, 
  MapPin,
  Clock,
  Star,
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  Target,
  Award,
  Zap,
  Globe
} from 'lucide-react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Fade,
  Slide,
  Paper,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';

const AnalyticsCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  }
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 12,
  padding: theme.spacing(2),
  textAlign: 'center',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
  }
}));

const IconContainer = styled(Box)(({ theme, color }) => ({
  width: 56,
  height: 56,
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${color} 0%, ${color}80 100%)`,
  boxShadow: `0 4px 20px ${color}40`,
  margin: '0 auto',
  marginBottom: theme.spacing(2),
}));

const RideAnalytics = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockAnalytics = {
        overview: {
          totalRides: 156,
          totalSavings: 12450,
          averageRating: 4.8,
          totalConnects: 89,
          monthlyRides: 24,
          monthlySavings: 1850,
          carbonSaved: 245,
          timeSaved: 89
        },
        trends: {
          ridesGrowth: 12.5,
          savingsGrowth: 8.3,
          ratingGrowth: 2.1,
          connectsGrowth: -3.2,
          carbonGrowth: 15.7,
          timeGrowth: 6.8
        },
        monthlyData: [
          { month: 'Jan', rides: 18, savings: 1200, connects: 12 },
          { month: 'Feb', rides: 22, savings: 1450, connects: 15 },
          { month: 'Mar', rides: 19, savings: 1350, connects: 13 },
          { month: 'Apr', rides: 25, savings: 1650, connects: 18 },
          { month: 'May', rides: 28, savings: 1850, connects: 20 },
          { month: 'Jun', rides: 24, savings: 1600, connects: 11 }
        ],
        topRoutes: [
          { route: 'Home → Office', frequency: 45, savings: 3200, rating: 4.9 },
          { route: 'Airport → Home', frequency: 12, savings: 1800, rating: 4.7 },
          { route: 'Mall → Home', frequency: 8, savings: 600, rating: 4.5 },
          { route: 'Gym → Home', frequency: 15, savings: 900, rating: 4.8 }
        ],
        achievements: [
          { title: 'First Ride', description: 'Completed your first ride share', achieved: true, date: '2024-01-15' },
          { title: 'Savings Master', description: 'Saved over ₹10,000', achieved: true, date: '2024-03-20' },
          { title: 'Social Butterfly', description: 'Connected with 50+ people', achieved: false, progress: 75 },
          { title: 'Eco Warrior', description: 'Saved 200kg of CO2', achieved: false, progress: 60 },
          { title: 'Rating Champion', description: 'Maintained 4.5+ rating for 3 months', achieved: true, date: '2024-05-10' }
        ],
        insights: [
          { type: 'savings', message: 'You save ₹415 on average per month', icon: DollarSign, color: '#10b981' },
          { type: 'time', message: 'You save 3 hours per week on average', icon: Clock, color: '#6366f1' },
          { type: 'environment', message: 'You\'ve saved 245kg of CO2 emissions', icon: Globe, color: '#f59e0b' },
          { type: 'social', message: 'You\'ve met 89 new people through ride sharing', icon: Users, color: '#ec4899' }
        ]
      };
      
      setAnalytics(mockAnalytics);
      setLoading(false);
    };

    loadAnalytics();
  }, []);

  const getTrendIcon = (value) => {
    return value > 0 ? <TrendingUp size={16} color="#10b981" /> : <TrendingDown size={16} color="#ef4444" />;
  };

  const getTrendColor = (value) => {
    return value > 0 ? '#10b981' : '#ef4444';
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Loading analytics...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Slide direction="up" in timeout={600}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
            Ride Analytics
          </Typography>
          <Typography variant="body1" sx={{ color: '#6b7280', mb: 4 }}>
            Detailed insights into your ride sharing journey
          </Typography>

          {/* Overview Metrics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={800}>
                <AnalyticsCard>
                  <CardContent>
                    <IconContainer color="#6366f1">
                      <Car size={28} color="white" />
                    </IconContainer>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {analytics.overview.totalRides}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      Total Rides
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      {getTrendIcon(analytics.trends.ridesGrowth)}
                      <Typography variant="caption" sx={{ color: getTrendColor(analytics.trends.ridesGrowth) }}>
                        +{analytics.trends.ridesGrowth}%
                      </Typography>
                    </Box>
                  </CardContent>
                </AnalyticsCard>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={900}>
                <AnalyticsCard>
                  <CardContent>
                    <IconContainer color="#10b981">
                      <DollarSign size={28} color="white" />
                    </IconContainer>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      ₹{analytics.overview.totalSavings.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      Total Saved
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      {getTrendIcon(analytics.trends.savingsGrowth)}
                      <Typography variant="caption" sx={{ color: getTrendColor(analytics.trends.savingsGrowth) }}>
                        +{analytics.trends.savingsGrowth}%
                      </Typography>
                    </Box>
                  </CardContent>
                </AnalyticsCard>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={1000}>
                <AnalyticsCard>
                  <CardContent>
                    <IconContainer color="#f59e0b">
                      <Star size={28} color="white" />
                    </IconContainer>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {analytics.overview.averageRating}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      Average Rating
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      {getTrendIcon(analytics.trends.ratingGrowth)}
                      <Typography variant="caption" sx={{ color: getTrendColor(analytics.trends.ratingGrowth) }}>
                        +{analytics.trends.ratingGrowth}%
                      </Typography>
                    </Box>
                  </CardContent>
                </AnalyticsCard>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={1100}>
                <AnalyticsCard>
                  <CardContent>
                    <IconContainer color="#a855f7">
                      <Users size={28} color="white" />
                    </IconContainer>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {analytics.overview.totalConnects}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      Total Connects
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      {getTrendIcon(analytics.trends.connectsGrowth)}
                      <Typography variant="caption" sx={{ color: getTrendColor(analytics.trends.connectsGrowth) }}>
                        {analytics.trends.connectsGrowth}%
                      </Typography>
                    </Box>
                  </CardContent>
                </AnalyticsCard>
              </Fade>
            </Grid>
          </Grid>

          {/* Tabs for different analytics sections */}
          <Box sx={{ mb: 4 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                }
              }}
            >
              <Tab label="Insights" />
              <Tab label="Achievements" />
              <Tab label="Top Routes" />
              <Tab label="Environmental Impact" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {activeTab === 0 && (
            <Slide direction="left" in timeout={400}>
              <Box>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Key Insights
                </Typography>
                <Grid container spacing={3}>
                  {analytics.insights.map((insight, index) => {
                    const IconComponent = insight.icon;
                    return (
                      <Grid item xs={12} sm={6} key={index}>
                        <Fade in timeout={600 + index * 100}>
                          <MetricCard>
                            <IconContainer color={insight.color}>
                              <IconComponent size={24} color="white" />
                            </IconContainer>
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                              {insight.message}
                            </Typography>
                          </MetricCard>
                        </Fade>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Slide>
          )}

          {activeTab === 1 && (
            <Slide direction="left" in timeout={400}>
              <Box>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Achievements
                </Typography>
                <Grid container spacing={2}>
                  {analytics.achievements.map((achievement, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Fade in timeout={600 + index * 100}>
                        <AnalyticsCard>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Box sx={{ 
                                color: achievement.achieved ? '#10b981' : '#6b7280',
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                {achievement.achieved ? <Award size={24} /> : <Target size={24} />}
                              </Box>
                              <Chip 
                                label={achievement.achieved ? 'Achieved' : 'In Progress'}
                                color={achievement.achieved ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                              {achievement.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                              {achievement.description}
                            </Typography>
                            {achievement.achieved ? (
                              <Typography variant="caption" sx={{ color: '#10b981' }}>
                                Achieved on {achievement.date}
                              </Typography>
                            ) : (
                              <Box>
                                <Typography variant="caption" sx={{ color: '#6b7280', mb: 1, display: 'block' }}>
                                  Progress: {achievement.progress}%
                                </Typography>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={achievement.progress}
                                  sx={{ height: 6, borderRadius: 3 }}
                                />
                              </Box>
                            )}
                          </CardContent>
                        </AnalyticsCard>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Slide>
          )}

          {activeTab === 2 && (
            <Slide direction="left" in timeout={400}>
              <Box>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Top Routes
                </Typography>
                <Grid container spacing={2}>
                  {analytics.topRoutes.map((route, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Fade in timeout={600 + index * 100}>
                        <AnalyticsCard>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {route.route}
                              </Typography>
                              <Chip 
                                label={`${route.frequency} rides`}
                                size="small"
                                color="primary"
                              />
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DollarSign size={16} color="#10b981" />
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  ₹{route.savings} saved
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Star size={16} color="#f59e0b" />
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  {route.rating} rating
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </AnalyticsCard>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Slide>
          )}

          {activeTab === 3 && (
            <Slide direction="left" in timeout={400}>
              <Box>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Environmental Impact
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Fade in timeout={600}>
                      <AnalyticsCard>
                        <CardContent>
                          <IconContainer color="#10b981">
                            <Globe size={28} color="white" />
                          </IconContainer>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {analytics.overview.carbonSaved}kg
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                            CO2 Saved
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            {getTrendIcon(analytics.trends.carbonGrowth)}
                            <Typography variant="caption" sx={{ color: getTrendColor(analytics.trends.carbonGrowth) }}>
                              +{analytics.trends.carbonGrowth}%
                            </Typography>
                          </Box>
                        </CardContent>
                      </AnalyticsCard>
                    </Fade>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Fade in timeout={700}>
                      <AnalyticsCard>
                        <CardContent>
                          <IconContainer color="#6366f1">
                            <Clock size={28} color="white" />
                          </IconContainer>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {analytics.overview.timeSaved}h
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                            Time Saved
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            {getTrendIcon(analytics.trends.timeGrowth)}
                            <Typography variant="caption" sx={{ color: getTrendColor(analytics.trends.timeGrowth) }}>
                              +{analytics.trends.timeGrowth}%
                            </Typography>
                          </Box>
                        </CardContent>
                      </AnalyticsCard>
                    </Fade>
                  </Grid>
                </Grid>
              </Box>
            </Slide>
          )}
        </Box>
      </Slide>
    </Box>
  );
};

export default RideAnalytics; 